import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';

const ENCRYPTIONS = ['ssl', 'tls', 'starttls', 'none'];

const emptyForm = {
  shop_id: '',
  name: '',
  imap_host: '',
  imap_port: 993,
  imap_encryption: 'ssl',
  imap_user: '',
  imap_password: '',
  inbox_folder: 'INBOX',
  sent_folder: '',
  import_since: '',
};

export default function LearningMailboxForm({ shops, mailbox, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() =>
    mailbox
      ? {
          ...emptyForm,
          ...mailbox,
          shop_id: mailbox.shop_id || '',
          sent_folder: mailbox.sent_folder || '',
          import_since: mailbox.import_since ? mailbox.import_since.slice(0, 10) : '',
          // Never round-trips from the API; blank means "leave it alone".
          imap_password: '',
        }
      : { ...emptyForm, shop_id: shops[0]?.id || '' }
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = { ...form, sent_folder: form.sent_folder || null, import_since: form.import_since || null };
    if (!payload.imap_password) delete payload.imap_password;

    try {
      if (mailbox) {
        await api.put(`/learning-mailboxes/${mailbox.id}`, payload);
      } else {
        await api.post('/learning-mailboxes', payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t('knowledge.mailbox_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const field = 'block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500';
  const label = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={label}>{t('knowledge.mailbox_name')} *</label>
          <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required className={field} />
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_shop')} *</label>
          <select value={form.shop_id} onChange={(e) => set('shop_id', e.target.value)} required className={field}>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <label className={label}>{t('knowledge.mailbox_host')} *</label>
          <input type="text" value={form.imap_host} onChange={(e) => set('imap_host', e.target.value)} required placeholder="imap.provider.nl" className={field} />
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_port')}</label>
          <input type="number" value={form.imap_port} onChange={(e) => set('imap_port', Number(e.target.value))} className={field} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={label}>{t('knowledge.mailbox_encryption')}</label>
          <select value={form.imap_encryption} onChange={(e) => set('imap_encryption', e.target.value)} className={field}>
            {ENCRYPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_user')} *</label>
          <input type="text" value={form.imap_user} onChange={(e) => set('imap_user', e.target.value)} required className={field} />
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_password')} {!mailbox && '*'}</label>
          <input
            type="password"
            value={form.imap_password}
            onChange={(e) => set('imap_password', e.target.value)}
            required={!mailbox}
            placeholder={mailbox ? t('knowledge.mailbox_password_unchanged') : ''}
            className={field}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={label}>{t('knowledge.mailbox_inbox_folder')}</label>
          <input type="text" value={form.inbox_folder} onChange={(e) => set('inbox_folder', e.target.value)} className={field} />
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_sent_folder')}</label>
          <input type="text" value={form.sent_folder} onChange={(e) => set('sent_folder', e.target.value)} placeholder={t('knowledge.mailbox_auto_detect')} className={field} />
        </div>
        <div>
          <label className={label}>{t('knowledge.mailbox_import_since')}</label>
          <input type="date" value={form.import_since} onChange={(e) => set('import_since', e.target.value)} className={field} />
        </div>
      </div>

      <p className="text-xs text-gray-500">{t('knowledge.mailbox_folder_hint')}</p>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          {t('common.cancel')}
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors">
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
